import orderBy from "lodash.orderby";
import startCase from "lodash.startcase";
import { DateTime } from "luxon";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceCategory,
  HistoryActionType,
  HistoryDto,
} from "@intelligems/ig-types";
import { officialCategoryName } from "@intelligems/ig-utils";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import { Drawer, Grid, Skeleton, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useGetExperience } from "@src/api/experiences/get-experience";
import { useGetSourceExperience } from "@src/api/experiences/get-source-experience";
import { useGetHistory } from "@src/api/histories/get-history";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";
import { closeDrawer, getDrawer } from "@src/store/slices/drawers";
import { theme } from "@src/theme";

const PREFIX = "Drawer";

const classes = {
  title: `${PREFIX}-title`,
};

const StyledDrawer = styled(Drawer)({
  "& .MuiDrawer-paper": {
    width: "600px",
    borderRadius: "0px",
    paddingTop: "40px",
    paddingBottom: "40px",
  },
  [`& .${classes.title}`]: {
    fontSize: "20px",
    fontWeight: 600,
  },
  zIndex: "1400",
});

interface HistoryTimelineProps {
  events: HistoryDto[];
  type: "experiment" | "campaign";
  sourceExperienceId: string | null | undefined;
  experienceCategory?: ExperienceCategory;
}

const EventMap: Record<HistoryActionType, { title: string; color: string }> = {
  endExperience: {
    title: "Ended #Item",
    color: theme.palette.text.tonal.error,
  },
  pauseExperience: {
    title: "Paused #Item",
    color: theme.palette.text.tonal.warning,
  },
  startExperience: {
    title: "Started #Item",
    color: theme.palette.text.tonal.success,
  },
  duplicateExperience: {
    title: "Duplicated #Item",
    color: theme.palette.text.tonal.info,
  },
  deleteExperience: {
    title: "Deleted #Item",
    color: theme.palette.text.tonal.error,
  },
  saveExperience: {
    title: "Saved #Item",
    color: theme.palette.text.tonal.info,
  },
  enableCampaign: {
    title: "Enabled Experience",
    color: theme.palette.text.tonal.success,
  },
  archiveCampaign: {
    title: "Archived Experience",
    color: theme.palette.text.tonal.error,
  },
  disableCampaign: {
    title: "Disabled Experience",
    color: theme.palette.text.tonal.warning,
  },
  saveCampaign: {
    title: "Saved Experience",
    color: theme.palette.text.tonal.info,
  },
  confirmIntegration: {
    title: "Confirmed Integration & QA",
    color: theme.palette.text.tonal.success,
  },
  rolloutExperience: {
    title: "Rolled Out From A/B Test",
    color: theme.palette.text.tonal.info,
  },
  archiveExperience: {
    title: "Archived Experience",
    color: theme.palette.text.tonal.error,
  },
  unArchiveExperience: {
    title: "Unarchived Experience",
    color: theme.palette.text.tonal.warning,
  },
};

const HistoryTimeline = ({
  events,
  sourceExperienceId,
  type,
  experienceCategory,
}: HistoryTimelineProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const sortedEvents = useMemo(() => {
    return orderBy(events, (event: HistoryDto) => event.lastUpdateTs, "desc");
  }, [events]);
  const sourceExperience = useGetSourceExperience(
    sourceExperienceId || undefined
  );

  const handleDrawerClose = () => {
    dispatch(closeDrawer("history"));
  };

  return (
    <Timeline position="left">
      {sortedEvents.map((event: HistoryDto) => {
        let name;
        if (event.user?.firstName) {
          name = event.user.firstName;
        }
        if (event.user?.lastName) {
          name += ` ${event.user.lastName}`;
        }

        if (!name) {
          name = event.user?.email || "";
        }

        return (
          <TimelineItem key={event.lastUpdateTs.toString()}>
            <TimelineOppositeContent color={theme.palette.text.secondary}>
              <IgTypography>
                {`${EventMap[event.action].title.replace("#Item", startCase(type))}`}
                {event.action === "rolloutExperience" && sourceExperienceId && (
                  <IgTypography
                    igStyle={sourceExperience?.data ? "link" : undefined}
                    color={
                      sourceExperience?.data
                        ? undefined
                        : theme.palette.text.secondary
                    }
                    onClick={() => {
                      if (sourceExperience?.data) {
                        handleDrawerClose();
                        void navigate(
                          `${EXPERIMENT_ROUTES.Experiment}/${sourceExperienceId}?action=edit&`
                        );
                      }
                    }}
                  >
                    <br />
                    {sourceExperience?.data?.experience.name ||
                      `deleted ${
                        experienceCategory
                          ? officialCategoryName(experienceCategory, false)
                          : "experiment"
                      }`}
                  </IgTypography>
                )}
              </IgTypography>
              <br />
              <IgTypography color={theme.palette.text.secondary}>
                {`${DateTime.fromMillis(event.lastUpdateTs).toLocaleString(
                  DateTime.DATETIME_MED
                )}`}
              </IgTypography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              {/* @ts-ignore*/}
              <TimelineDot
                sx={{ backgroundColor: EventMap[event.action].color }}
              />
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <IgTypography>{name}</IgTypography>
              {event.user?.email.includes("intelligems") && (
                <>
                  <br />
                  <IgTypography color={"placeholder"}>Intelligems</IgTypography>
                </>
              )}
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

export const HistoryDrawer = () => {
  const drawer = useSelector(getDrawer("history"));
  const { id } = useParams<{ id: string }>();

  const history = useGetHistory({
    body: undefined,
    pathParams: { experienceId: (drawer?.experienceInfo?.id || id) ?? "" },
    queryParams: undefined,
  });

  const dispatch = useDispatch();
  const handleDrawerClose = () => dispatch(closeDrawer("history"));

  if (!drawer) return null;

  return (
    <StyledDrawer anchor={"right"} open={true} onClose={handleDrawerClose}>
      <Grid
        container
        direction={"column"}
        justifyContent={"space-between"}
        sx={{ height: "100%", width: "100%", maxHeight: "100%" }}
      >
        <Grid container item direction={"column"} alignItems={"center"}>
          <Grid item>
            <IgTypography variant={"h5"}>
              {drawer.experienceType === "experiment"
                ? "Experiment"
                : "Experience"}{" "}
              History
            </IgTypography>
          </Grid>
          {history.isLoading ? (
            <Grid item sx={{ width: "100%" }}>
              <Skeleton
                sx={{
                  height: "50px",
                  marginLeft: "20px",
                  marginRight: "20px",
                  paddingRight: "20px",
                }}
              />
              <Skeleton
                sx={{
                  height: "50px",
                  marginLeft: "20px",
                  marginRight: "20px",
                  paddingRight: "20px",
                }}
              />
              <Skeleton
                sx={{
                  height: "50px",
                  marginLeft: "20px",
                  marginRight: "20px",
                  paddingRight: "20px",
                }}
              />
            </Grid>
          ) : history.data?.history.length ? (
            <Grid
              item
              sx={{ width: "100%", maxHeight: "95%", overflowY: "scroll" }}
            >
              <HistoryTimeline
                sourceExperienceId={drawer?.experienceInfo?.sourceExperienceId}
                events={history.data.history}
                type={
                  drawer.experienceType === "experiment"
                    ? "experiment"
                    : "campaign"
                }
                experienceCategory={drawer?.experienceInfo?.category}
              />
            </Grid>
          ) : (
            <Grid item alignItems={"center"} sx={{ paddingTop: "10px" }}>
              <IgTypography variant={"h6"}>No History Available</IgTypography>
            </Grid>
          )}
        </Grid>
        <Grid
          item
          container
          direction={"row"}
          justifyContent={"center"}
          sx={{ marginTop: "20px", marginBottom: "80px" }}
        >
          <IgButton variant={"contained"} onClick={() => handleDrawerClose()}>
            Close
          </IgButton>
        </Grid>
      </Grid>
    </StyledDrawer>
  );
};
